/**
 * Legal Hub Page
 *
 * Provides a central location for all legal documents including:
 * - Terms of Service
 * - Privacy Policy
 * - Acceptable Use Policy
 * - Cookie Policy
 * - AI Output Disclaimer
 * - Copyright Policy
 * - Data Processing Agreement
 * - Refund and Cancellation Policy
 * - Security & Data Protection Statement
 */

import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  DocumentTextIcon,
  ShieldCheckIcon,
  CheckBadgeIcon,
  GlobeAltIcon,
  ExclamationTriangleIcon,
  DocumentDuplicateIcon,
  ServerIcon,
  CreditCardIcon,
  LockClosedIcon,
  ArrowRightIcon,
} from '@heroicons/react/24/outline';
import { MainLayout } from '../layouts/MainLayout';
import { useMetaTags } from '../hooks/useMetaTags';
import legalDocuments from '../data/legal-documents.json';

// Map icon names to actual icon components
const iconMap: Record<string, React.ComponentType<{ className?: string }>> = {
  DocumentTextIcon,
  ShieldCheckIcon,
  CheckBadgeIcon,
  GlobeAltIcon,
  ExclamationTriangleIcon,
  DocumentDuplicateIcon,
  ServerIcon,
  CreditCardIcon,
  LockClosedIcon,
};

export default function LegalPage() {
  useMetaTags({
    title: 'Legal | Smart Prompt',
    url: 'https://www.smartprompt.us/legal'
  });

  return (
    <MainLayout>
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 px-4 py-8">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="mb-12"
          >
            <h1 className="text-4xl font-bold text-white mb-4">
              Legal Documents
            </h1>
            <p className="text-lg text-slate-300">
              Review our terms, policies, and agreements that govern your use of SmartPrompt.
            </p>
          </motion.div>

          {/* Document Cards Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {legalDocuments.map((doc, index) => {
              const IconComponent = iconMap[doc.icon];

              return (
                <motion.div
                  key={doc.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                >
                  <Link
                    to={doc.url}
                    className="block group"
                  >
                    <div className="h-full bg-slate-800/30 backdrop-blur-sm border border-slate-700/50 rounded-2xl p-6 hover:bg-slate-800/50 hover:border-slate-600/50 transition-all duration-300 hover:shadow-lg hover:shadow-blue-500/10">
                      {/* Icon */}
                      <div className="mb-4">
                        <div className="w-12 h-12 bg-gradient-to-br from-blue-500/20 to-purple-500/20 rounded-xl flex items-center justify-center group-hover:from-blue-500/30 group-hover:to-purple-500/30 transition-all duration-300">
                          {IconComponent && (
                            <IconComponent className="w-6 h-6 text-blue-400" />
                          )}
                        </div>
                      </div>

                      {/* Title */}
                      <h2 className="text-xl font-semibold text-white mb-2 group-hover:text-blue-400 transition-colors duration-300">
                        {doc.title}
                      </h2>

                      {/* Description */}
                      <p className="text-slate-400 text-sm mb-4 line-clamp-2">
                        {doc.description}
                      </p>

                      {/* Footer with last updated and arrow */}
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-slate-500">
                          Last updated: {doc.lastUpdated}
                        </span>
                        <ArrowRightIcon className="w-4 h-4 text-slate-500 group-hover:text-blue-400 group-hover:translate-x-1 transition-all duration-300" />
                      </div>
                    </div>
                  </Link>
                </motion.div>
              );
            })}
          </div>

          {/* Footer Note */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.8 }}
            className="mt-12 text-center"
          >
            <p className="text-slate-500 text-sm">
              For questions about our legal documents, please contact us through the Support channel in your account.
            </p>
          </motion.div>
        </div>
      </div>
    </MainLayout>
  );
}
